<template>
  <div :id="editorId" class="email-editor" style="position: relative;">
    <vue-editor
      ref="editor"
      v-model="contenido"
      :editorOptions="editorOptions"
      :editor-toolbar="customToolbar"
      :placeholder="placeholder"
      @input="handleInput"
    />
    <div v-if="uploadingImage" class="upload-overlay">
      <b-spinner small />
      <span class="ml-2">Subiendo imagen...</span>
    </div>

    <!-- Modal: link opcional para imagen insertada -->
    <b-modal
      v-model="showImageLinkModal"
      title="Agregar link a la imagen"
      ok-title="Insertar"
      cancel-title="Omitir"
      centered
      @ok="confirmarLinkImagen(imageLinkUrl)"
      @cancel="cancelarLinkImagen"
      @hide="cancelarLinkImagen"
    >
      <p class="mb-2">Puedes asociar un enlace a la imagen para que sea cliqueable en el correo. Déjalo vacío si no necesitas enlace.</p>
      <b-form-input
        v-model="imageLinkUrl"
        placeholder="https://ejemplo.com"
        type="url"
        autofocus
        @keyup.enter.native="confirmarLinkImagen(imageLinkUrl)"
      />
    </b-modal>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import imageCompression from 'browser-image-compression';
import { uploadCampaignImage } from '@/services/campaignService';

export default {
  name: 'EmailEditor',
  components: { VueEditor },
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Escribe aquí...'
    },
    minHeight: {
      type: Number,
      default: 180
    }
  },
  data() {
    return {
      editorId: `email-editor-${Math.random().toString(36).substr(2, 9)}`,
      contenido: this.value,
      uploadingImage: false,
      showImageLinkModal: false,
      imageLinkUrl: '',
      imageLinkResolve: null,
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image']
      ]
    };
  },
  computed: {
    editorOptions() {
      return {
        bounds: `#${this.editorId}`
      };
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.contenido) {
        this.contenido = newVal;
      }
    }
  },
  mounted() {
    // Aplicar altura mínima al editor
    if (this.$refs.editor && this.$refs.editor.quill) {
      const { quill } = this.$refs.editor;
      const { container } = quill;
      const editorElement = container.querySelector('.ql-editor');
      if (editorElement) {
        editorElement.style.minHeight = `${this.minHeight}px`;
      }

      // Escuchar el evento de foco en el editor Quill
      quill.on('selection-change', (range) => {
        if (range !== null) {
          this.$emit('focus');
        }
      });

      // Registrar handler de imagen personalizado (upload en vez de base64)
      const toolbar = quill.getModule('toolbar');
      toolbar.addHandler('image', () => this.imageUploadHandler(quill));
    }
  },
  methods: {
    handleInput(value) {
      this.$emit('input', value);
    },
    /**
     * Inserta texto en la posición del cursor
     * @param {string} tag - Etiqueta a insertar
     */
    insertarEtiqueta(tag) {
      if (this.$refs.editor && this.$refs.editor.quill) {
        const { quill } = this.$refs.editor;
        const range = quill.getSelection();
        const position = range ? range.index : quill.getLength();
        quill.insertText(position, tag);
        // Mover el cursor después de la etiqueta insertada
        quill.setSelection(position + tag.length);
        // Enfocar el editor
        quill.focus();
      }
    },
    /**
     * Enfoca el editor
     */
    focus() {
      if (this.$refs.editor && this.$refs.editor.quill) {
        this.$refs.editor.quill.focus();
      }
    },
    /**
     * Abre el modal para pedir un link opcional a la imagen.
     * Retorna la URL introducida o null si se omite/cancela.
     */
    pedirLinkImagen() {
      return new Promise((resolve) => {
        this.imageLinkUrl = '';
        this.imageLinkResolve = resolve;
        this.showImageLinkModal = true;
      });
    },
    confirmarLinkImagen(url) {
      if (!this.imageLinkResolve) return;
      this.showImageLinkModal = false;
      const resolve = this.imageLinkResolve;
      this.imageLinkResolve = null;
      resolve(url?.trim() || null);
    },
    cancelarLinkImagen() {
      if (!this.imageLinkResolve) return;
      this.showImageLinkModal = false;
      const resolve = this.imageLinkResolve;
      this.imageLinkResolve = null;
      resolve(null);
    },
    /**
     * Maneja la subida de imágenes al editor
     */
    async imageUploadHandler(quill) {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          this.$bvToast.toast('Tipo de archivo no permitido. Use JPG, PNG, GIF o WEBP.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
          return;
        }

        this.uploadingImage = true;
        try {
          // Opciones de compresión: Máximo 1MB y redimensionar a un máximo de 1280px
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1280,
            useWebWorker: true,
            fileType: file.type,
          };

          // Comprimir imagen
          const compressedFile = await imageCompression(file, options);

          // Subir archivo comprimido
          const { url } = await uploadCampaignImage(compressedFile);

          const range = quill.getSelection(true);
          const linkUrl = await this.pedirLinkImagen();

          if (linkUrl) {
            const html = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer"><img src="${url}"/></a>`;
            quill.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
          } else {
            quill.insertEmbed(range.index, 'image', url, 'user');
          }
          quill.setSelection(range.index + 1);
        } catch (error) {
          const msg = error.response?.data?.error || 'Error al procesar o subir la imagen.';
          this.$bvToast.toast(msg, {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        } finally {
          this.uploadingImage = false;
        }
      };
    }
  }
};
</script>

<style scoped>
.email-editor {
  width: 100%;
}

.email-editor >>> .quillWrapper {
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  overflow: visible;
}

.email-editor >>> .ql-toolbar {
  background: transparent;
  border: none;
  padding: 10px;
}

.email-editor >>> .ql-container {
  border: none;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
}

.email-editor >>> .ql-editor {
  padding: 15px;
  line-height: 1.6;
}

.email-editor >>> .ql-editor.ql-blank::before {
  color: #9CA3AF;
  font-style: normal;
  font-size: 14px;
}

.email-editor >>> .ql-editor p {
  margin-bottom: 10px;
}

.email-editor >>> .ql-editor:focus {
  outline: none;
}

.upload-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}
</style>
